/**
 * 🚀 UNIFIED VOICEACTOR DATA SLICE
 * 
 * Clean zone-based architecture modeled after bookWorksheetDataSlice.js
 * Eliminates cross-zone contamination and provides isolated data management.
 * 
 * Architecture:
 * - actorZone: Pure actor operations (no portfolio data)
 * - portfolioZone: Pure portfolio operations (no character data)
 * - characterZone: Pure character operations (no variation data)  
 * - variationZone: Pure variation operations (no character data)
 * - sharedData: Read-only shared data (actors, portfolios, selections)
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient } from '../../apiTools/apiClient';
import { config } from '../../constants/config';

// ===== ASYNC THUNKS (DIRECT API CALLS - NO PASS-THROUGH) =====

// Actor Zone Thunks
export const fetchActors = createAsyncThunk(
  'voiceActorWorksheetData/fetchActors',
  async (_, { rejectWithValue }) => {
    try {
      console.log('[VA_DATA] Fetching voice actors');
      const response = await apiClient.get(config.VOICE_ACTORS_BASE);
      console.log('[VA_DATA] Actors fetched successfully:', response.data);
      return response.data;
    } catch (error) {
      console.log('[VA_DATA] Error fetching actors:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchActorProfile = createAsyncThunk(
  'voiceActorWorksheetData/fetchActorProfile',
  async (actorId, { rejectWithValue }) => {
    try {
      console.log('[VA_DATA] Fetching actor profile:', actorId);
      // Using existing endpoint from voiceActorSlice
      const response = await apiClient.get(config.GET_API_DIRECT_VOICE_ACTOR_PROFILE, { actorId });
      return { actorId, profile: response.data };
    } catch (error) {
      console.error('[VA_DATA] Error fetching actor profile:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createActor = createAsyncThunk(
  'voiceActorWorksheetData/createActor',
  async (data, { rejectWithValue }) => {
    try {
      console.log('[VA_DATA] Creating actor:', data);
      const response = await apiClient.post(config.VOICE_ACTORS_BASE, data);
      console.log('[VA_DATA] Actor created successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('[VA_DATA] Error creating actor:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateActor = createAsyncThunk(
  'voiceActorWorksheetData/updateActor',
  async ({ actorId, data }, { rejectWithValue }) => {
    try {
      console.log('[VA_DATA] Updating actor:', actorId, data);
      const response = await apiClient.put(config.GET_API_DIRECT_VOICE_ACTOR_PROFILE, data, { actorId });
      console.log('[VA_DATA] Actor updated successfully:', response.data);
      return { actorId, ...response.data };
    } catch (error) {
      console.error('[VA_DATA] Error updating actor:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteActor = createAsyncThunk(
  'voiceActorWorksheetData/deleteActor',
  async (actorId, { rejectWithValue }) => {
    try {
      console.log('[VA_DATA] Deleting actor:', actorId);
      const response = await apiClient.delete(config.GET_API_DIRECT_VOICE_ACTOR_PROFILE, { actorId });
      console.log('[VA_DATA] Actor deleted successfully:', actorId);
      return { actorId, ...response.data };
    } catch (error) {
      console.error('[VA_DATA] Error deleting actor:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Portfolio Zone Thunks
export const fetchPortfolios = createAsyncThunk(
  'voiceActorWorksheetData/fetchPortfolios',
  async (actorId, { rejectWithValue }) => {
    try {
      console.log('[VA_DATA] Fetching portfolios for actor:', actorId);
      // Using existing endpoint from voiceActorSlice
      const response = await apiClient.get(config.GET_API_DIRECT_PORTFOLIOS_BY_ACTOR, { actorId });

      return response.data.portfolios || response.data;
    } catch (error) {
      console.error('[VA_DATA] Error fetching portfolios:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchPortfolioProfile = createAsyncThunk(
  'voiceActorWorksheetData/fetchPortfolioProfile',
  async (portfolioId, { rejectWithValue }) => {
    try {
      console.log('[VA_DATA] Fetching portfolio profile:', portfolioId);
      // TODO: API_MISSING - May need to adapt from existing portfolio endpoints
      const response = await apiClient.get(config.GET_API_DIRECT_PORTFOLIO_PROFILE, { portfolioId });
      return { portfolioId, profile: response.data };
    } catch (error) {
      console.error('[VA_DATA] Error fetching portfolio profile:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createPortfolio = createAsyncThunk(
  'voiceActorData/createPortfolio',
  async ({ actorId, data }, { rejectWithValue }) => {
    try {
      console.log('[VA_DATA] Creating portfolio for actor:', actorId);
      // Using existing endpoint from voiceActorSlice
      const response = await apiClient.post(config.POST_API_CREATE_PORTFOLIO, data, { actorId });
      return response.data;
    } catch (error) {
      console.error('[VA_DATA] Error creating portfolio:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updatePortfolio = createAsyncThunk(
  'voiceActorData/updatePortfolio',
  async ({ portfolioId, data }, { rejectWithValue }) => {
    try {
      console.log('[VA_DATA] Updating portfolio:', portfolioId);
      // Using existing endpoint from voiceActorSlice
      const response = await apiClient.put(config.PUT_API_UPDATE_PORTFOLIO, data, { portfolioId });
      return { portfolioId, ...response.data };
    } catch (error) {
      console.error('[VA_DATA] Error updating portfolio:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deletePortfolio = createAsyncThunk(
  'voiceActorData/deletePortfolio',
  async (portfolioId, { rejectWithValue }) => {
    try {
      console.log('[VA_DATA] Deleting portfolio:', portfolioId);
      // Using existing endpoint from voiceActorSlice
      const response = await apiClient.delete(config.DELETE_API_PORTFOLIO, { portfolioId });
      return { portfolioId, ...response.data };
    } catch (error) {
      console.error('[VA_DATA] Error deleting portfolio:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Character Zone Thunks (EXACT SAME PATTERN as BookWorksheet)
export const fetchCharacters = createAsyncThunk(
  'voiceActorData/fetchCharacters',
  async (portfolioId, { rejectWithValue }) => {
    try {
      console.log('[VA_DATA] Fetching characters for portfolio:', portfolioId);
      // Using existing endpoint from voiceActorSlice
      const response = await apiClient.get(config.GET_API_DIRECT_CHARACTERS_BY_PORTFOLIO, { portfolioId });
      return response.data.characters || response.data;
    } catch (error) {
      console.error('[VA_DATA] Error fetching characters:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchCharacter = createAsyncThunk(
  'voiceActorData/fetchCharacter',
  async ({ characterId, portfolioId }, { rejectWithValue }) => {
    try {
      console.log('[VA_DATA] Fetching character:', characterId);
      // Using existing endpoint from voiceActorSlice
      const response = await apiClient.get(config.GET_API_DIRECT_CHARACTER_BY_PORTFOLIO, {
        portfolioId,
        characterId
      });
      return response.data;
    } catch (error) {
      console.error('[VA_DATA] Error fetching character:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createCharacter = createAsyncThunk(
  'voiceActorData/createCharacter',
  async ({ portfolioId, data }, { rejectWithValue }) => {
    try {
      console.log('[VA_DATA] Creating character for portfolio:', portfolioId);
      // Using existing endpoint from voiceActorSlice
      const response = await apiClient.post(config.POST_API_ADD_CHAR_TO_PORTFOLIO, data, { portfolioId });
      return response.data;
    } catch (error) {
      console.error('[VA_DATA] Error creating character:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateCharacter = createAsyncThunk(
  'voiceActorData/updateCharacter',
  async ({ characterId, portfolioId, data }, { rejectWithValue }) => {
    try {
      console.log('[VA_DATA] Updating character:', characterId);
      // Using existing endpoint from voiceActorSlice
      const response = await apiClient.put(config.PUT_API_DIRECT_CHARACTER_BY_PORTFOLIO, data, {
        portfolioId,
        characterId
      });
      return { characterId, ...response.data };
    } catch (error) {
      console.error('[VA_DATA] Error updating character:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteCharacter = createAsyncThunk(
  'voiceActorData/deleteCharacter',
  async ({ characterId, portfolioId, options = {} }, { rejectWithValue }) => {
    try {
      console.log('[VA_DATA] Deleting character via unified delete system:', characterId);

      // Use unified delete endpoint with VoiceActor entity type
      const response = await apiClient.delete(`${config.DELETE_API_VA_CHARACTER_UNIFIED}/${characterId}`,
        {
          params: {
            portfolioId,
            cleanup: options.cleanup || 'cleanup',
            cascade: options.cascade || 'cascade',
            dryRun: options.dryRun || false,
            force: options.force || false,
            ...options
          }
        }
      );

      console.log('[VA_DATA] Unified delete response:', response.data);
      return {
        characterId,
        ...response.data,
        // Map unified response to expected format
        deletedCount: response.data.deletedCount,
        s3FilesDeleted: response.data.s3FilesDeleted || [],
        cascadeResults: response.data.cascadeResults || {}
      };
    } catch (error) {
      console.error('[VA_DATA] Error deleting character via unified system:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Variation Zone Thunks (EXACT SAME PATTERN as BookWorksheet)
export const fetchVariations = createAsyncThunk(
  'voiceActorData/fetchVariations',
  async ({ characterId, portfolioId }, { rejectWithValue }) => {
    try {
      console.log('[VA_DATA] Fetching variations for character:', characterId, 'in portfolio:', portfolioId);
      const response = await apiClient.get(config.GET_VA_CHARACTER_VARIATIONS, {
        portfolioId,
        characterId
      });
      const variations = response.data.variations?.map(variation => ({
        ...variation,
        characterId
      })) || [];
      return { variations, characterId };
    } catch (error) {
      console.error('[VA_DATA] Error fetching variations:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);



export const fetchVariationCounts = createAsyncThunk(
  'voiceActorData/fetchVariationCounts',
  async (characterId, { rejectWithValue }) => {
    try {
      console.log('[VA_DATA] Fetching variation counts for character:', characterId);

      // NOTE: VoiceActor API differs from BookWorksheet pattern:
      // - BookWorksheet uses query parameter: GET /api/get-book-char-variation-counts?charUuid=abc123
      // - VoiceActor uses query parameter but different endpoint: GET /api/characters/:characterId/variation-counts?characterId=abc123
      // The server controller expects req.query.characterId, so we send it as a query parameter
      const response = await apiClient.get(`${config.GET_VA_VARIATION_COUNTS}?characterId=${characterId}`);

      const counts = response.data.counts || response.data.variationCounts || response.data || {};
      console.log('[VA_DATA] Received variation counts:', counts);

      return { characterId, counts };
    } catch (error) {
      console.error('[VA_DATA] Error fetching variation counts:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Load a specific version for editing (from VersionsList row/edit/checkbox)
 */
export const loadVersionForEdit = createAsyncThunk(
  'voiceActorData/loadVersionForEdit',
  async ({ variationId, versionId, characterId, actorId, portfolioId }, { dispatch, rejectWithValue }) => {
    try {
      console.log(`[VA_DATA_SLICE] Loading version ${versionId} for variation ${variationId}`);

      // First load the variation for edit to get all samples
      const variationResult = await dispatch(loadVariationForEdit({
        variationId, characterId, actorId, portfolioId
      }));

      if (loadVariationForEdit.rejected.match(variationResult)) {
        throw new Error('Failed to load variation data');
      }

      // Find the specific version in the samples
      const samples = variationResult.payload.samples || [];
      const targetVersion = samples.find(sample => sample.uuid === versionId);

      if (!targetVersion) {
        throw new Error(`Version ${versionId} not found in variation samples`);
      }

      return {
        ...variationResult.payload,
        activeVersion: {
          uuid: targetVersion.uuid,
          textSnippet: targetVersion.textSnippet,
          s3Url: targetVersion.s3Url,
          fileDuration: targetVersion.fileDuration,
          version: targetVersion.version,
          isActive: true
        }
      };
    } catch (error) {
      console.error('[VA_DATA_SLICE] Error loading version for edit:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Load version for cloning (from Use as Base button)
 */
export const loadVersionForClone = createAsyncThunk(
  'voiceActorData/loadVersionForClone',
  async ({ variationId, versionId, characterId, actorId, portfolioId }, { dispatch, rejectWithValue }) => {
    try {
      console.log(`[VA_DATA_SLICE] Loading version ${versionId} for cloning`);

      // Load the version data first
      const versionResult = await dispatch(loadVersionForEdit({
        variationId, versionId, characterId, actorId, portfolioId
      }));

      if (loadVersionForEdit.rejected.match(versionResult)) {
        throw new Error('Failed to load version data for cloning');
      }

      const { variation, activeVersion } = versionResult.payload;

      // Create a new variation structure for cloning
      const clonedVariation = {
        uuid: null, // Will be set when created
        name: `${variation.name} (Copy)`,
        content: activeVersion.textSnippet,
        activeSampleUuid: null,
        priority: variation.priority || 'medium',
        status: 'not-started',
        deadline: null,
        version: 1
      };

      // Create a new active version for the clone
      const clonedActiveVersion = {
        uuid: null,
        textSnippet: activeVersion.textSnippet,
        s3Url: '', // Reset S3 URL for new variation
        fileDuration: 0,
        version: 1,
        isActive: false
      };

      return {
        variation: clonedVariation,
        activeVersion: clonedActiveVersion,
        samples: [],
        characterId,
        actorId,
        portfolioId
      };
    } catch (error) {
      console.error('[VA_DATA_SLICE] Error loading version for cloning:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const loadVariationForEdit = createAsyncThunk(
  'voiceActorWorksheetData/loadVariationForEdit',
  async ({ variationId, characterId, actorId, portfolioId }, { getState, rejectWithValue }) => {
    try {
      console.log('[VA_DATA] Loading variation for edit:', variationId);

      // Get variation from state for reference (not requirement)
      const state = getState();
      const currentVariations = state.user.voiceActorWorksheetData?.variationZone?.variations || [];
      const currentVariation = currentVariations.find(v => v.uuid === variationId);

      // ALWAYS make API call to get full variation data with samples
      // FIXED:
      const response = await apiClient.get(config.GET_VA_VARIATION_SAMPLES, {
        variationId  // URL path parameter
      }, {
        actorId,     // Query parameters
        portfolioId,
        characterId
      });


      console.log('[VA_DATA] Variation samples API response:', response.data);

      // FIXED: Server now returns exact BookWorksheet format with characterList
      const characterData = response.data.characterList?.[0];
      if (!characterData) {
        console.error('[VA_DATA] Character data not found in API response');
        throw new Error('Character data not found');
      }

      // Find the variation in the response - now using BookWorksheet structure
      const variationItem = characterData.variations?.find(v =>
        (v.variation?.uuid === variationId) || (v.uuid === variationId)
      );

      if (!variationItem) {
        // If not found in API response, use current variation data as fallback
        if (currentVariation) {
          console.warn('[VA_DATA] Variation not found in API response, using current state data');
          return {
            variation: {
              uuid: currentVariation.uuid,
              name: currentVariation.name,
              content: currentVariation.content || '',
              activeSampleUuid: currentVariation.activeSampleUuid || null,
              priority: currentVariation.priority || 'medium',
              status: currentVariation.status || 'not-started',
              deadline: currentVariation.deadline || null,
              version: currentVariation.version || 1
            },
            samples: [],
            characterId,
            actorId,
            portfolioId
          };
        } else {
          throw new Error('Variation not found in API response or current state');
        }
      }

      // Extract the actual variation object from the nested structure
      const variation = variationItem.variation || variationItem;

      // Process speech samples from the nested structure
      // FIXED: For VoiceActor, use the CharacterVariation.version instead of speech sample version
      const samples = variationItem.speechSamples?.map(sample => {
        const speechSample = sample.speechSample || sample;
        const textSnippet = sample.textSnippets?.[0] || sample.textSnippet;
        const voiceSnippet = sample.voiceSnippets?.[0] || sample.voiceSnippet;

        return {
          uuid: speechSample.uuid,
          // FIXED: Use variation.version for VoiceActor instead of speech sample version
          // This ensures the version number matches the RecordedCharacter variation version
          version: variation.version || 1,
          s3Url: voiceSnippet?.s3Url || speechSample.s3Url || '',
          textSnippet: textSnippet?.content || textSnippet?.textSnippet || speechSample.content || 'No text available',
          createdAt: speechSample.createdAt,
          isActive: speechSample.uuid === variation.activeSampleUuid,
          fileDuration: voiceSnippet?.fileDuration || speechSample.fileDuration || 0
        };
      }).filter(sample => sample !== null) || [];

      // Extract content from the first sample's textSnippet if variation.content is empty
      const variationContent = variation.content ||
        (samples.length > 0 ? samples[0].textSnippet : '') ||
        currentVariation.content || '';

      return {
        variation: {
          uuid: variation.uuid,
          name: variation.name,
          content: variationContent,
          activeSampleUuid: variation.activeSampleUuid,
          priority: variation.priority || currentVariation.priority || 'medium',
          status: variation.status || currentVariation.status || 'not-started',
          deadline: variation.deadline || currentVariation.deadline || null,
          version: variation.version || currentVariation.version || 1
        },
        samples,
        characterId,
        actorId,
        portfolioId
      };
    } catch (error) {
      console.error('[VA_DATA] Error loading variation for edit:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createVariation = createAsyncThunk(
  'voiceActorData/createVariation',
  async ({ characterId, data }, { rejectWithValue }) => {
    try {
      console.log('[VA_DATA] Creating variation for character:', characterId);
      // Using existing endpoint from voiceActorSlice
      const response = await apiClient.post(config.POST_API_DIRECT_VARIATION, data, {
        characterId
      });
      return response.data;
    } catch (error) {
      console.error('[VA_DATA] Error creating variation:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateVariation = createAsyncThunk(
  'voiceActorData/updateVariation',
  async ({ variationId, actorId, portfolioId, characterId, data }, { rejectWithValue }) => {
    try {
      console.log('[VA_DATA] Updating variation:', variationId);
      // ✅ FIXED: Pass hierarchy parameters as query params to match server expectations
      const response = await apiClient.put(
        config.PUT_API_VA_DIRECT_VARIATION,
        data,
        { variationId }, // URL params for :variationId substitution
        { params: { actorId, portfolioId, characterId } } // ✅ FIXED: Query params for server validation
      );
      return { variationId, ...response.data };
    } catch (error) {
      console.error('[VA_DATA] Error updating variation:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteVariation = createAsyncThunk(
  'voiceActorData/deleteVariation',
  async (params, { rejectWithValue }) => {
    try {
      // ✅ ROBUST: Handle both object and direct parameter patterns
      let variationId, options = {};

      if (typeof params === 'string') {
        // Direct string parameter
        variationId = params;
      } else if (params && typeof params === 'object') {
        if (params.variationId) {
          // Standard pattern: { variationId: 'string', options: {} }
          if (typeof params.variationId === 'string') {
            variationId = params.variationId;
            options = params.options || {};
          } else if (params.variationId.variationId) {
            // Nested pattern: { variationId: { variationId: 'string' } }
            variationId = params.variationId.variationId;
            options = params.options || {};
          } else {
            // Object passed as variationId: { variationId: 'string' }
            variationId = params.variationId;
            options = {};
          }
        } else {
          throw new Error(`Invalid parameter structure: ${JSON.stringify(params)}`);
        }
      } else {
        throw new Error(`Invalid variationId parameter: ${JSON.stringify(params)}`);
      }

      console.log('[VA_DATA] Deleting variation:', variationId);
      console.log('[VA_DATA] DELETE_API_VA_VARIATION_UNIFIED URL:', config.DELETE_API_VA_VARIATION_UNIFIED);
      console.log('[VA_DATA] Extracted variationId:', variationId, 'from params:', params);

      // ✅ STANDARDIZED: Now matches BookWorksheet pattern exactly
      const response = await apiClient.delete(`${config.DELETE_API_VA_VARIATION_UNIFIED}/${variationId}`, {
        params: {
          cleanup: options.cleanup || 'cleanup',    // Delete S3 files (audio/text)
          cascade: options.cascade || 'cascade',    // Delete all speech samples
          dryRun: options.dryRun || false,          // Support dry-run
          force: options.force || false,            // Support force delete
          ...options                                // Support additional options
        }
      });
      return { variationId, ...response.data };
    } catch (error) {
      console.error('[VA_DATA] Error deleting variation:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteVersion = createAsyncThunk(
  'voiceActorData/deleteVersion',
  async ({ versionId, variationId }, { rejectWithValue }) => {
    try {
      console.log('[VA_DATA] Deleting version:', versionId);
      // ✅ FIXED: Use config constant instead of hardcoded URL
      const response = await apiClient.delete(`${config.DELETE_API_VA_SPEECH_SAMPLE_UNIFIED}/${versionId}`, {
        params: {
          cleanup: 'cleanup'     // Delete S3 files (audio/text)
        }
      });
      return { versionId, variationId, ...response.data };
    } catch (error) {
      console.error('[VA_DATA] Error deleting version:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Additional thunks for variation management (EXACT SAME as BookWorksheet)
export const loadDefaultVersion = createAsyncThunk(
  'voiceActorData/loadDefaultVersion',
  async ({ characterId }, { getState, rejectWithValue }) => {
    try {
      console.log('[VA_DATA] Loading default version for character:', characterId);

      const state = getState();
      const sharedData = state.user.voiceActorWorksheetData?.sharedData;
      const actorId = sharedData?.selections?.actorId;
      const portfolioId = sharedData?.selections?.portfolioId;

      if (!actorId || !portfolioId) {
        throw new Error('Missing actorId or portfolioId for default version');
      }

      // Create default variation structure
      const defaultVariation = {
        uuid: null, // Will be set when created
        name: '',
        content: '',
        activeSampleUuid: null,
        priority: 'medium',
        status: 'not-started',
        deadline: null,
        version: 1
      };

      // Create default active version
      const defaultActiveVersion = {
        uuid: null,
        textSnippet: '',
        s3Url: '',
        fileDuration: 0,
        version: 1,
        isActive: false
      };

      return {
        variation: defaultVariation,
        activeVersion: defaultActiveVersion,
        samples: [],
        characterId,
        actorId,
        portfolioId
      };
    } catch (error) {
      console.error('[VA_DATA] Error loading default version:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const saveVariation = createAsyncThunk(
  'voiceActorData/saveVariation',
  async ({ variationData, versionData, editMode, characterId }, { getState, rejectWithValue }) => {
    try {
      console.log('[VA_DATA] Saving variation in mode:', editMode);

      // Get the current selections from state
      const state = getState();
      const selections = state.user.voiceActorWorksheetData?.sharedData?.selections;
      const actorId = selections?.actorId;
      const portfolioId = selections?.portfolioId;

      if (editMode === 'create' || editMode === 'clone') {
        // Create new variation
        const createData = {
          name: variationData.name,
          content: versionData.textSnippet,
          s3Url: '', // ✅ FIXED: Required by server - empty for new variations
          priority: variationData.priority || 'medium',
          status: variationData.status || 'not-started',
          deadline: variationData.deadline,
          version: variationData.version || 1,
          fileDuration: 0 // ✅ FIXED: Required by server - default for new variations
        };

        const response = await apiClient.post(config.CREATE_CHARACTER_VARIATION, createData, {
          characterId
        });

        return {
          variation: response.data,
          isCreate: true,
          editMode
        };
      } else {
        // Update existing variation - FIXED: Add s3Url and fileDuration + Neo4j-safe data types
        const updateData = {
          name: variationData.name,
          content: versionData.textSnippet,
          s3Url: variationData.s3Url || '', // ✅ FIXED: Include s3Url for updates
          priority: variationData.priority || 'medium',
          status: variationData.status || 'not-started',
          deadline: variationData.deadline ? new Date(variationData.deadline).toISOString() : null,
          version: parseInt(variationData.version) || 1,
          fileDuration: variationData.fileDuration || 0 // ✅ FIXED: Include fileDuration for updates
        };

        const response = await apiClient.put(
          config.PUT_API_VA_DIRECT_VARIATION,
          updateData,
          { variationId: variationData.uuid }, // URL params for :variationId substitution
          { params: { actorId, portfolioId, characterId } } // Query params in axios config
        );

        return {
          variation: response.data.variation, // ✅ Extract variation from server response
          variationId: variationData.uuid,
          isCreate: false,
          editMode
        };
      }
    } catch (error) {
      console.error('[VA_DATA] Error saving variation:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ===== INITIAL STATE =====
const initialState = {
  // Actor Zone (Pure - no portfolio data)
  actorZone: {
    actors: [],
    selectedActor: null,
    loading: false,
    error: null,
    lastUpdated: null
  },

  // Portfolio Zone (Pure - no character data)
  portfolioZone: {
    portfolios: [],
    activePortfolio: null,
    loading: false,
    error: null,
    lastUpdated: null
  },

  // Character Zone (Pure - no variation data) - EXACT SAME as BookWorksheet
  characterZone: {
    characters: [],
    selectedCharacter: null,
    loading: false,
    error: null,
    lastUpdated: null
  },

  // Variation Zone (Pure - no character data except ID) - EXACT SAME as BookWorksheet
  variationZone: {
    variations: [],
    activeVariation: null,
    activeVersion: null,
    samples: [],
    variationCounts: {},
    loading: false,
    error: null,
    lastUpdated: null,

    // Edit state
    editMode: 'view', // 'view' | 'edit' | 'create'
    hasUnsavedChanges: false,
    isSubmitting: false
  },

  // Shared Data (Read-only shared state)
  sharedData: {
    actors: [],
    portfolios: [],
    actorProfiles: {},
    portfolioProfiles: {},

    // Current selections
    selections: {
      actorId: null,
      portfolioId: null,
      characterId: null,
      variationId: null
    },

    // Loading states
    loading: {
      actors: false,
      portfolios: false,
      characters: false,
      variations: false,
      profiles: false
    },

    // Errors
    errors: {
      actors: null,
      portfolios: null,
      characters: null,
      variations: null,
      profiles: null
    },

    lastUpdated: {
      actors: null,
      portfolios: null,
      characters: null,
      variations: null,
      profiles: null
    }
  }
};

// ===== SLICE DEFINITION =====
const voiceActorDataSlice = createSlice({
  name: 'voiceActorWorksheetData',
  initialState,
  reducers: {
    // ===== SELECTION ACTIONS =====
    setActorSelection: (state, action) => {
      const actorId = action.payload;
      state.sharedData.selections.actorId = actorId;

      // Clear dependent selections
      state.sharedData.selections.portfolioId = null;
      state.sharedData.selections.characterId = null;
      state.sharedData.selections.variationId = null;

      console.log('[VA_DATA] Set actor selection:', actorId);
    },

    // Auto-select first portfolio when portfolios are fetched
    autoSelectFirstPortfolio: (state) => {
      const portfolios = state.sharedData.portfolios;
      if (portfolios && portfolios.length > 0 && !state.sharedData.selections.portfolioId) {
        // Find a portfolio with a profile or select the first one
        const portfolioWithProfile = portfolios.find(portfolio => !portfolio.profileMissing);
        const selectedPortfolio = portfolioWithProfile || portfolios[0];

        state.sharedData.selections.portfolioId = selectedPortfolio.uuid;
        console.log('[VA_DATA] Auto-selected portfolio:', selectedPortfolio.uuid, selectedPortfolio.name);
      }
    },

    setPortfolioSelection: (state, action) => {
      const portfolioId = action.payload;
      state.sharedData.selections.portfolioId = portfolioId;

      // Clear dependent selections
      state.sharedData.selections.characterId = null;
      state.sharedData.selections.variationId = null;

      console.log('[VA_DATA] Set portfolio selection:', portfolioId);
    },

    setCharacterSelection: (state, action) => {
      const characterId = action.payload;

      // Prevent duplicate selections
      if (state.sharedData.selections.characterId === characterId) {
        console.log('[VA_DATA] Skipping duplicate character selection:', characterId);
        return; // No state change = no re-renders
      }

      state.sharedData.selections.characterId = characterId;

      // Clear dependent selections
      state.sharedData.selections.variationId = null;

      // Update selected character in character zone
      state.characterZone.selectedCharacter = state.characterZone.characters.find(
        char => char.uuid === characterId
      ) || null;

      console.log('[VA_DATA] Set character selection:', characterId);
    },

    setVariationSelection: (state, action) => {
      const variationId = action.payload;
      state.sharedData.selections.variationId = variationId;

      if (variationId === null) {
        // FIXED: Clear all variation-related data when deselecting (for "New Variation" button)
        state.variationZone.activeVariation = null;
        state.variationZone.activeVersion = null;
        state.variationZone.samples = [];
        state.variationZone.editMode = 'view';
        state.variationZone.hasUnsavedChanges = false;
        console.log('[VA_DATA] Cleared variation selection and all related data');
      } else {
        // Update active variation in variation zone
        state.variationZone.activeVariation = state.variationZone.variations.find(
          variation => variation.uuid === variationId
        ) || null;
        console.log('[VA_DATA] Set variation selection:', variationId);
      }
    },

    // ===== VARIATION ZONE ACTIONS (EXACT SAME as BookWorksheet) =====
    updateVersionField: (state, action) => {
      const { field, value } = action.payload;
      if (state.variationZone.activeVersion) {
        state.variationZone.activeVersion[field] = value;
        state.variationZone.hasUnsavedChanges = true;
      }
    },

    setActiveVersionFromSamples: (state, action) => {
      const sampleUuid = action.payload;
      const sample = state.variationZone.samples.find(s => s.uuid === sampleUuid);

      if (sample) {
        state.variationZone.activeVersion = {
          uuid: sample.uuid,
          textSnippet: sample.textSnippet,
          s3Url: sample.s3Url,
          fileDuration: sample.fileDuration,
          version: sample.version,
          isActive: true
        };

        // Update active sample UUID in variation
        if (state.variationZone.activeVariation) {
          state.variationZone.activeVariation.activeSampleUuid = sampleUuid;
        }

        console.log('[VA_DATA] Set active version from samples:', sampleUuid);
      }
    },

    setEditMode: (state, action) => {
      state.variationZone.editMode = action.payload;
      console.log('[VA_DATA] Set edit mode:', action.payload);
    },

    clearUnsavedChanges: (state) => {
      state.variationZone.hasUnsavedChanges = false;
    },

    setSubmitting: (state, action) => {
      state.variationZone.isSubmitting = action.payload;
    },

    // ===== CLEAR ACTIONS =====
    clearActorZone: (state) => {
      state.actorZone.actors = [];
      state.actorZone.selectedActor = null;
      state.actorZone.error = null;
    },

    clearPortfolioZone: (state) => {
      state.portfolioZone.portfolios = [];
      state.portfolioZone.activePortfolio = null;
      state.portfolioZone.error = null;
    },

    clearCharacterZone: (state) => {
      state.characterZone.characters = [];
      state.characterZone.selectedCharacter = null;
      state.characterZone.error = null;
    },

    clearVariationZone: (state) => {
      state.variationZone.variations = [];
      state.variationZone.activeVariation = null;
      state.variationZone.activeVersion = null;
      state.variationZone.samples = [];
      state.variationZone.editMode = 'view';
      state.variationZone.hasUnsavedChanges = false;
      state.variationZone.error = null;
    },

    // ===== FORM MANAGEMENT ACTIONS (from BookWorksheet) =====
    updateFormField: (state, action) => {
      const { field, value } = action.payload;
      if (state.variationZone.activeVariation) {
        state.variationZone.activeVariation[field] = value;
        state.variationZone.hasUnsavedChanges = true;
      }
    },

    setFormDirty: (state, action) => {
      state.variationZone.hasUnsavedChanges = action.payload;
    },

    setFormSubmitting: (state, action) => {
      state.variationZone.isSubmitting = action.payload;
    },

    setEditError: (state, action) => {
      const { error } = action.payload;
      state.variationZone.error = error;
    },

    clearEditError: (state) => {
      state.variationZone.error = null;
    },

    clearEditForm: (state) => {
      state.variationZone.activeVariation = null;
      state.variationZone.activeVersion = null;
      state.variationZone.samples = [];
      state.variationZone.editMode = 'view';
      state.variationZone.hasUnsavedChanges = false;
      state.variationZone.isSubmitting = false;
      state.variationZone.error = null;
    },

    setRepositoryVersion: (state, action) => {
      state.variationZone.repositoryVersion = action.payload;
    },

    showErrorHint: (state, action) => {
      state.variationZone.errorHint = action.payload;
    },

    updateActiveSample: (state, action) => {
      const { variationId, sampleId } = action.payload;

      // Update the variation's active sample UUID
      if (state.variationZone.activeVariation?.uuid === variationId) {
        state.variationZone.activeVariation.activeSampleUuid = sampleId;
      }

      // Update the variation in the variations list
      const variationIndex = state.variationZone.variations.findIndex(v => v.uuid === variationId);
      if (variationIndex >= 0) {
        state.variationZone.variations[variationIndex].activeSampleUuid = sampleId;
      }

      // Update samples to reflect the new active state
      state.variationZone.samples = state.variationZone.samples.map(sample => ({
        ...sample,
        isActive: sample.uuid === sampleId
      }));

      console.log('[VA_DATA] Updated active sample:', sampleId, 'for variation:', variationId);
    },

    updateTextSnippetDb: (state, action) => {
      const { sampleuuid, content } = action.payload;

      // Update the sample in the samples array
      const sampleIndex = state.variationZone.samples.findIndex(s => s.uuid === sampleuuid);
      if (sampleIndex >= 0) {
        state.variationZone.samples[sampleIndex].textSnippet = content;
      }

      // Update active version if it matches
      if (state.variationZone.activeVersion?.uuid === sampleuuid) {
        state.variationZone.activeVersion.textSnippet = content;
      }

      console.log('[VA_DATA] Updated text snippet in database for sample:', sampleuuid);
    }
  },

  extraReducers: (builder) => {
    builder
      // ===== ACTORS =====
      .addCase(fetchActors.pending, (state) => {
        state.sharedData.loading.actors = true;
        state.sharedData.errors.actors = null;
      })
      .addCase(fetchActors.fulfilled, (state, action) => {
        state.sharedData.loading.actors = false;
        state.sharedData.actors = action.payload;
        state.actorZone.actors = action.payload;
        state.sharedData.lastUpdated.actors = Date.now();
        console.log('[VA_DATA] Stored actors in new slice:', action.payload.length, 'actors');
      })
      .addCase(fetchActors.rejected, (state, action) => {
        state.sharedData.loading.actors = false;
        state.sharedData.errors.actors = action.payload;
      })

      // ===== ACTOR PROFILES =====
      .addCase(fetchActorProfile.pending, (state) => {
        state.sharedData.loading.profiles = true;
        state.sharedData.errors.profiles = null;
      })
      .addCase(fetchActorProfile.fulfilled, (state, action) => {
        const { actorId, profile } = action.payload;
        state.sharedData.loading.profiles = false;
        state.sharedData.actorProfiles[actorId] = profile;
        state.sharedData.lastUpdated.profiles = Date.now();
        console.log('[VA_DATA] Stored actor profile for:', actorId);
      })
      .addCase(fetchActorProfile.rejected, (state, action) => {
        state.sharedData.loading.profiles = false;
        state.sharedData.errors.profiles = action.payload;
      })

      // ===== CREATE ACTOR =====
      .addCase(createActor.pending, (state) => {
        state.actorZone.loading = true;
        state.actorZone.error = null;
      })
      .addCase(createActor.fulfilled, (state, action) => {
        state.actorZone.loading = false;
        const newActor = action.payload;
        state.actorZone.actors.push(newActor);
        state.sharedData.actors.push(newActor);
        state.actorZone.selectedActor = newActor;
        state.actorZone.lastUpdated = Date.now();
        console.log('[VA_DATA] Actor created successfully:', newActor.uuid);
      })
      .addCase(createActor.rejected, (state, action) => {
        state.actorZone.loading = false;
        state.actorZone.error = action.payload;
      })

      // ===== UPDATE ACTOR =====
      .addCase(updateActor.pending, (state) => {
        state.actorZone.loading = true;
        state.actorZone.error = null;
      })
      .addCase(updateActor.fulfilled, (state, action) => {
        state.actorZone.loading = false;
        const { actorId, ...updatedData } = action.payload;

        // Update in actorZone
        const actorIndex = state.actorZone.actors.findIndex(a => a.uuid === actorId);
        if (actorIndex >= 0) {
          state.actorZone.actors[actorIndex] = { ...state.actorZone.actors[actorIndex], ...updatedData };
        }

        // Update in sharedData
        const sharedIndex = state.sharedData.actors.findIndex(a => a.uuid === actorId);
        if (sharedIndex >= 0) {
          state.sharedData.actors[sharedIndex] = { ...state.sharedData.actors[sharedIndex], ...updatedData };
        }

        // Update selected actor if it matches
        if (state.actorZone.selectedActor?.uuid === actorId) {
          state.actorZone.selectedActor = { ...state.actorZone.selectedActor, ...updatedData };
        }

        state.actorZone.lastUpdated = Date.now();
        console.log('[VA_DATA] Actor updated successfully:', actorId);
      })
      .addCase(updateActor.rejected, (state, action) => {
        state.actorZone.loading = false;
        state.actorZone.error = action.payload;
      })

      // ===== DELETE ACTOR =====
      .addCase(deleteActor.pending, (state) => {
        state.actorZone.loading = true;
        state.actorZone.error = null;
      })
      .addCase(deleteActor.fulfilled, (state, action) => {
        state.actorZone.loading = false;
        const { actorId } = action.payload;

        // Remove from actorZone
        state.actorZone.actors = state.actorZone.actors.filter(a => a.uuid !== actorId);

        // Remove from sharedData
        state.sharedData.actors = state.sharedData.actors.filter(a => a.uuid !== actorId);

        // Clear selected actor if it was deleted
        if (state.actorZone.selectedActor?.uuid === actorId) {
          state.actorZone.selectedActor = null;
        }

        // Clear actor selection if it was deleted
        if (state.sharedData.selections.actorId === actorId) {
          state.sharedData.selections.actorId = null;
          state.sharedData.selections.portfolioId = null;
          state.sharedData.selections.characterId = null;
          state.sharedData.selections.variationId = null;
        }

        state.actorZone.lastUpdated = Date.now();
        console.log('[VA_DATA] Actor deleted successfully:', actorId);
      })
      .addCase(deleteActor.rejected, (state, action) => {
        state.actorZone.loading = false;
        state.actorZone.error = action.payload;
      })

      // ===== PORTFOLIOS =====
      .addCase(fetchPortfolios.pending, (state) => {
        state.sharedData.loading.portfolios = true;
        state.sharedData.errors.portfolios = null;
      })
      .addCase(fetchPortfolios.fulfilled, (state, action) => {
        state.sharedData.loading.portfolios = false;
        state.sharedData.portfolios = action.payload;
        state.portfolioZone.portfolios = action.payload;
        state.sharedData.lastUpdated.portfolios = Date.now();
      })
      .addCase(fetchPortfolios.rejected, (state, action) => {
        state.sharedData.loading.portfolios = false;
        state.sharedData.errors.portfolios = action.payload;
      })

      // ===== PORTFOLIO PROFILES =====
      .addCase(fetchPortfolioProfile.pending, (state) => {
        state.sharedData.loading.profiles = true;
        state.sharedData.errors.profiles = null;
      })
      .addCase(fetchPortfolioProfile.fulfilled, (state, action) => {
        const { portfolioId, profile } = action.payload;
        state.sharedData.loading.profiles = false;
        state.sharedData.portfolioProfiles[portfolioId] = profile;
        state.sharedData.lastUpdated.profiles = Date.now();
        console.log('[VA_DATA] Stored portfolio profile for:', portfolioId);
      })
      .addCase(fetchPortfolioProfile.rejected, (state, action) => {
        state.sharedData.loading.profiles = false;
        state.sharedData.errors.profiles = action.payload;
      })

      // ===== CREATE PORTFOLIO =====
      .addCase(createPortfolio.pending, (state) => {
        state.portfolioZone.loading = true;
        state.portfolioZone.error = null;
      })
      .addCase(createPortfolio.fulfilled, (state, action) => {
        state.portfolioZone.loading = false;
        const newPortfolio = action.payload;
        state.portfolioZone.portfolios.push(newPortfolio);
        state.sharedData.portfolios.push(newPortfolio);
        state.portfolioZone.activePortfolio = newPortfolio;
        state.portfolioZone.lastUpdated = Date.now();
        console.log('[VA_DATA] Portfolio created successfully:', newPortfolio.uuid);
      })
      .addCase(createPortfolio.rejected, (state, action) => {
        state.portfolioZone.loading = false;
        state.portfolioZone.error = action.payload;
      })

      // ===== UPDATE PORTFOLIO =====
      .addCase(updatePortfolio.pending, (state) => {
        state.portfolioZone.loading = true;
        state.portfolioZone.error = null;
      })
      .addCase(updatePortfolio.fulfilled, (state, action) => {
        state.portfolioZone.loading = false;
        const { portfolioId, ...updatedData } = action.payload;

        // Update in portfolioZone
        const portfolioIndex = state.portfolioZone.portfolios.findIndex(p => p.uuid === portfolioId);
        if (portfolioIndex >= 0) {
          state.portfolioZone.portfolios[portfolioIndex] = { ...state.portfolioZone.portfolios[portfolioIndex], ...updatedData };
        }

        // Update in sharedData
        const sharedIndex = state.sharedData.portfolios.findIndex(p => p.uuid === portfolioId);
        if (sharedIndex >= 0) {
          state.sharedData.portfolios[sharedIndex] = { ...state.sharedData.portfolios[sharedIndex], ...updatedData };
        }

        // Update active portfolio if it matches
        if (state.portfolioZone.activePortfolio?.uuid === portfolioId) {
          state.portfolioZone.activePortfolio = { ...state.portfolioZone.activePortfolio, ...updatedData };
        }

        state.portfolioZone.lastUpdated = Date.now();
        console.log('[VA_DATA] Portfolio updated successfully:', portfolioId);
      })
      .addCase(updatePortfolio.rejected, (state, action) => {
        state.portfolioZone.loading = false;
        state.portfolioZone.error = action.payload;
      })

      // ===== DELETE PORTFOLIO =====
      .addCase(deletePortfolio.pending, (state) => {
        state.portfolioZone.loading = true;
        state.portfolioZone.error = null;
      })
      .addCase(deletePortfolio.fulfilled, (state, action) => {
        state.portfolioZone.loading = false;
        const { portfolioId } = action.payload;

        // Remove from portfolioZone
        state.portfolioZone.portfolios = state.portfolioZone.portfolios.filter(p => p.uuid !== portfolioId);

        // Remove from sharedData
        state.sharedData.portfolios = state.sharedData.portfolios.filter(p => p.uuid !== portfolioId);

        // Clear active portfolio if it was deleted
        if (state.portfolioZone.activePortfolio?.uuid === portfolioId) {
          state.portfolioZone.activePortfolio = null;
        }

        // Clear portfolio selection if it was deleted
        if (state.sharedData.selections.portfolioId === portfolioId) {
          state.sharedData.selections.portfolioId = null;
          state.sharedData.selections.characterId = null;
          state.sharedData.selections.variationId = null;
        }

        state.portfolioZone.lastUpdated = Date.now();
        console.log('[VA_DATA] Portfolio deleted successfully:', portfolioId);
      })
      .addCase(deletePortfolio.rejected, (state, action) => {
        state.portfolioZone.loading = false;
        state.portfolioZone.error = action.payload;
      })

      // ===== CHARACTERS =====
      .addCase(fetchCharacters.pending, (state) => {
        state.characterZone.loading = true;
        state.characterZone.error = null;
      })
      .addCase(fetchCharacters.fulfilled, (state, action) => {
        state.characterZone.loading = false;
        state.characterZone.characters = action.payload;
        state.characterZone.lastUpdated = Date.now();
      })
      .addCase(fetchCharacters.rejected, (state, action) => {
        state.characterZone.loading = false;
        state.characterZone.error = action.payload;
      })

      // ===== SINGLE CHARACTER =====
      .addCase(fetchCharacter.fulfilled, (state, action) => {
        const character = action.payload;
        const index = state.characterZone.characters.findIndex(c => c.uuid === character.uuid);

        if (index >= 0) {
          state.characterZone.characters[index] = character;
        } else {
          state.characterZone.characters.push(character);
        }

        // Update selected character if it matches
        if (state.sharedData.selections.characterId === character.uuid) {
          state.characterZone.selectedCharacter = character;
        }
      })

      // ===== CREATE CHARACTER =====
      .addCase(createCharacter.fulfilled, (state, action) => {
        const newCharacter = action.payload;
        state.characterZone.characters.push(newCharacter);
      })

      // ===== UPDATE CHARACTER =====
      .addCase(updateCharacter.fulfilled, (state, action) => {
        const { characterId, ...updatedData } = action.payload;
        const index = state.characterZone.characters.findIndex(c => c.uuid === characterId);

        if (index >= 0) {
          state.characterZone.characters[index] = { ...state.characterZone.characters[index], ...updatedData };
        }

        // Update selected character if it matches
        if (state.characterZone.selectedCharacter?.uuid === characterId) {
          state.characterZone.selectedCharacter = { ...state.characterZone.selectedCharacter, ...updatedData };
        }
      })

      // ===== DELETE CHARACTER =====
      .addCase(deleteCharacter.pending, (state) => {
        state.characterZone.loading = true;
        state.characterZone.error = null;
      })
      .addCase(deleteCharacter.fulfilled, (state, action) => {
        state.characterZone.loading = false;
        const { characterId } = action.payload;

        // Remove from characters list
        state.characterZone.characters = state.characterZone.characters.filter(c => c.uuid !== characterId);

        // Clear selected character if it was deleted
        if (state.characterZone.selectedCharacter?.uuid === characterId) {
          state.characterZone.selectedCharacter = null;
        }

        // Clear character selection if it was deleted
        if (state.sharedData.selections.characterId === characterId) {
          state.sharedData.selections.characterId = null;
          state.sharedData.selections.variationId = null;
        }

        state.characterZone.lastUpdated = Date.now();
        console.log('[VA_DATA] Character deleted successfully:', characterId);
      })
      .addCase(deleteCharacter.rejected, (state, action) => {
        state.characterZone.loading = false;
        state.characterZone.error = action.payload;
      })

      // ===== VARIATIONS =====
      .addCase(fetchVariations.pending, (state) => {
        state.variationZone.loading = true;
        state.variationZone.error = null;
      })
      .addCase(fetchVariations.fulfilled, (state, action) => {
        state.variationZone.loading = false;
        state.variationZone.variations = action.payload.variations;
        state.variationZone.lastUpdated = Date.now();
        console.log('[VA_DATA] Stored variations for character:', action.payload.characterId, action.payload.variations.length, 'variations');
      })
      .addCase(fetchVariations.rejected, (state, action) => {
        state.variationZone.loading = false;
        state.variationZone.error = action.payload;
      })

      // ===== VARIATION COUNTS =====
      .addCase(fetchVariationCounts.pending, (state) => {
        console.log('[VA_DATA] fetchVariationCounts pending...');
      })
      .addCase(fetchVariationCounts.fulfilled, (state, action) => {
        const { counts } = action.payload;
        state.variationZone.variationCounts = counts;
      })
      .addCase(fetchVariationCounts.rejected, (state, action) => {
        console.error('[VA_DATA] ❌ fetchVariationCounts failed:', action.payload);
        state.variationZone.error = action.payload;
      })

      // ===== LOAD VARIATION FOR EDIT =====
      .addCase(loadVariationForEdit.pending, (state) => {
        state.variationZone.loading = true;
        state.variationZone.error = null;
      })
      .addCase(loadVariationForEdit.fulfilled, (state, action) => {
        const { variation, samples } = action.payload;

        state.variationZone.loading = false;
        state.variationZone.activeVariation = variation;
        state.variationZone.samples = samples || [];
        state.variationZone.editMode = 'edit';
        state.variationZone.hasUnsavedChanges = false;
        state.variationZone.lastUpdated = Date.now();

        // Sort samples by version
        state.variationZone.samples = (samples || []).sort((a, b) => {
          const versionA = parseInt(a.version) || 0;
          const versionB = parseInt(b.version) || 0;
          return versionA - versionB;
        });

        // Set active version from active sample or create a default one
        const activeSample = samples?.find(s => s.uuid === variation.activeSampleUuid);
        if (activeSample) {
          state.variationZone.activeVersion = {
            uuid: activeSample.uuid,
            textSnippet: activeSample.textSnippet,
            s3Url: activeSample.s3Url,
            fileDuration: activeSample.fileDuration,
            version: activeSample.version,
            isActive: true
          };
        } else {
          // Create a default active version if no samples exist
          state.variationZone.activeVersion = {
            uuid: null,
            textSnippet: variation.content || '',
            s3Url: '',
            fileDuration: 0,
            version: variation.version || 1,
            isActive: false
          };
        }

        console.log('[VA_DATA] Successfully loaded variation for edit:', {
          variation: variation.name,
          samples: samples?.length || 0,
          activeVersion: state.variationZone.activeVersion
        });
      })
      .addCase(loadVariationForEdit.rejected, (state, action) => {
        state.variationZone.loading = false;
        state.variationZone.error = action.payload;
      })

      // ===== CREATE VARIATION =====
      .addCase(createVariation.fulfilled, (state, action) => {
        const newVariation = action.payload;
        state.variationZone.variations.push(newVariation);
      })

      // ===== UPDATE VARIATION =====
      .addCase(updateVariation.fulfilled, (state, action) => {
        const { variationId, ...updatedData } = action.payload;
        const index = state.variationZone.variations.findIndex(v => v.uuid === variationId);

        if (index >= 0) {
          state.variationZone.variations[index] = { ...state.variationZone.variations[index], ...updatedData };
        }

        // Update active variation if it matches
        if (state.variationZone.activeVariation?.uuid === variationId) {
          state.variationZone.activeVariation = { ...state.variationZone.activeVariation, ...updatedData };
        }

        state.variationZone.hasUnsavedChanges = false;
      })

      // ===== DELETE VARIATION =====
      .addCase(deleteVariation.fulfilled, (state, action) => {
        const { variationId } = action.payload;

        // Remove from variations list
        state.variationZone.variations = state.variationZone.variations.filter(v => v.uuid !== variationId);

        // Clear active variation if it was deleted
        if (state.variationZone.activeVariation?.uuid === variationId) {
          state.variationZone.activeVariation = null;
          state.variationZone.activeVersion = null;
          state.variationZone.samples = [];
          state.variationZone.editMode = 'view';
        }
      })

      // ===== DELETE VERSION =====
      .addCase(deleteVersion.fulfilled, (state, action) => {
        const { versionId } = action.payload;

        // Remove from samples
        state.variationZone.samples = state.variationZone.samples.filter(s => s.uuid !== versionId);

        // Clear active version if it was deleted
        if (state.variationZone.activeVersion?.uuid === versionId) {
          state.variationZone.activeVersion = null;
        }
      })

      // ===== LOAD DEFAULT VERSION =====
      .addCase(loadDefaultVersion.pending, (state) => {
        state.variationZone.loading = true;
        state.variationZone.error = null;
      })
      .addCase(loadDefaultVersion.fulfilled, (state, action) => {
        const { variation, activeVersion } = action.payload;

        state.variationZone.loading = false;
        state.variationZone.activeVariation = variation;
        state.variationZone.activeVersion = activeVersion;
        state.variationZone.samples = [];
        state.variationZone.editMode = 'create';
        state.variationZone.hasUnsavedChanges = false;
        state.variationZone.lastUpdated = Date.now();

        console.log('[VA_DATA] Successfully loaded default version for new variation');
      })
      .addCase(loadDefaultVersion.rejected, (state, action) => {
        state.variationZone.loading = false;
        state.variationZone.error = action.payload;
      })

      // ===== SAVE VARIATION =====
      .addCase(saveVariation.pending, (state) => {
        state.variationZone.loading = true;
        state.variationZone.isSubmitting = true;
        state.variationZone.error = null;
      })
      .addCase(saveVariation.fulfilled, (state, action) => {
        const { variation, isCreate } = action.payload;

        state.variationZone.loading = false;
        state.variationZone.isSubmitting = false;
        state.variationZone.hasUnsavedChanges = false;
        state.variationZone.error = null;

        if (isCreate) {
          // Add new variation to list
          state.variationZone.variations.push(variation);
          // Update active variation with the new one
          state.variationZone.activeVariation = variation;
          // Switch to edit mode after creation
          state.variationZone.editMode = 'edit';
        } else {
          // Update existing variation in list
          const index = state.variationZone.variations.findIndex(v => v.uuid === variation.uuid);
          if (index >= 0) {
            state.variationZone.variations[index] = variation;
          }
          // Update active variation
          state.variationZone.activeVariation = variation;
        }

        state.variationZone.lastUpdated = Date.now();
        console.log('[VA_DATA] Successfully saved variation:', variation?.name, 'isCreate:', isCreate);
      })
      .addCase(saveVariation.rejected, (state, action) => {
        state.variationZone.loading = false;
        state.variationZone.isSubmitting = false;
        state.variationZone.error = action.payload;
      });
  }
});

// Export actions
export const {
  // Selection actions
  setActorSelection,
  autoSelectFirstPortfolio,
  setPortfolioSelection,
  setCharacterSelection,
  setVariationSelection,

  // Variation zone actions (EXACT SAME as BookWorksheet)
  updateVersionField,
  setActiveVersionFromSamples,
  setEditMode,
  clearUnsavedChanges,
  setSubmitting,

  // Form management actions
  updateFormField,
  setFormDirty,
  setFormSubmitting,
  setEditError,
  clearEditError,
  clearEditForm,
  setRepositoryVersion,
  showErrorHint,
  updateActiveSample,
  updateTextSnippetDb,

  // Clear actions
  clearActorZone,
  clearPortfolioZone,
  clearCharacterZone,
  clearVariationZone
} = voiceActorDataSlice.actions;

// ===== CHANGE COUNTER SELECTORS (for Stable Hooks) =====
export const selectActorZoneChangeCount = (state) => state.user.voiceActorWorksheetData?.actorZone?.lastUpdated || 0;
export const selectPortfolioZoneChangeCount = (state) => state.user.voiceActorWorksheetData?.portfolioZone?.lastUpdated || 0;
export const selectCharacterZoneChangeCount = (state) => state.user.voiceActorWorksheetData?.characterZone?.lastUpdated || 0;

export const selectVariationFormChangeCount = (state) => {
  const variationZone = state.user.voiceActorWorksheetData?.variationZone;
  return (variationZone?.lastUpdated || 0) +
    (variationZone?.hasUnsavedChanges ? 1 : 0) +
    (variationZone?.editMode === 'edit' ? 1 : 0);
};

export const selectVariationListChangeCount = (state) => state.user.voiceActorWorksheetData?.variationZone?.lastUpdated || 0;

export const selectVariationVersionsListChangeCount = (state) => {
  const variationZone = state.user.voiceActorWorksheetData?.variationZone;
  return (variationZone?.lastUpdated || 0) + (variationZone?.samples?.length || 0);
};

// Export reducer
export default voiceActorDataSlice.reducer;
